import type { CardAsset } from "./types";

export async function loadVerifiedSharedAsset(
  url: string,
  descriptor: CardAsset,
  signal: AbortSignal,
): Promise<string> {
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: descriptor.mimeType },
    cache: "no-store",
    credentials: "omit",
    redirect: "error",
    signal,
  });
  if (!response.ok) {
    try { await response.body?.cancel(); } catch { /* best effort */ }
    throw new Error("ASSET_UNAVAILABLE");
  }
  const contentType = response.headers.get("Content-Type")?.split(";", 1)[0]?.trim().toLowerCase();
  if (contentType !== descriptor.mimeType) {
    try { await response.body?.cancel(); } catch { /* best effort */ }
    throw new Error("INVALID_ASSET");
  }
  const declaredLength = response.headers.get("Content-Length");
  if (declaredLength !== null
      && (!/^(0|[1-9][0-9]*)$/u.test(declaredLength) || Number(declaredLength) !== descriptor.byteSize)) {
    try { await response.body?.cancel(); } catch { /* best effort */ }
    throw new Error("INVALID_ASSET");
  }
  const bytes = await readExactBytes(response, descriptor.byteSize, signal);
  if (detectedMimeType(bytes) !== descriptor.mimeType) throw new Error("INVALID_ASSET");
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  if (hexDigest(digest) !== descriptor.sha256) throw new Error("INVALID_ASSET");
  return URL.createObjectURL(new Blob([bytes], { type: descriptor.mimeType }));
}

async function readExactBytes(response: Response, expectedBytes: number, signal: AbortSignal): Promise<ArrayBuffer> {
  if (!Number.isSafeInteger(expectedBytes) || expectedBytes < 1 || expectedBytes > 32 * 1_024 * 1_024 || !response.body) {
    throw new Error("INVALID_ASSET");
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      total += value.byteLength;
      if (total > expectedBytes) throw new Error("INVALID_ASSET");
      chunks.push(value);
    }
  } catch (error) {
    try { await reader.cancel(); } catch { /* best effort */ }
    throw error;
  } finally {
    reader.releaseLock();
  }
  if (total !== expectedBytes) throw new Error("INVALID_ASSET");
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes.buffer;
}

function detectedMimeType(bytes: ArrayBuffer): CardAsset["mimeType"] | undefined {
  const value = new Uint8Array(bytes);
  if (value.length >= 3 && value[0] === 0xff && value[1] === 0xd8 && value[2] === 0xff) return "image/jpeg";
  if (value.length >= 8 && value[0] === 0x89 && value[1] === 0x50 && value[2] === 0x4e && value[3] === 0x47
      && value[4] === 0x0d && value[5] === 0x0a && value[6] === 0x1a && value[7] === 0x0a) return "image/png";
  if (value.length >= 12 && ascii(value, 0, 4) === "RIFF" && ascii(value, 8, 12) === "WEBP") return "image/webp";
  return undefined;
}

function ascii(bytes: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...bytes.slice(start, end));
}

function hexDigest(value: ArrayBuffer): string {
  return [...new Uint8Array(value)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
